import { Link, useMatches } from "@tanstack/react-router";

import { cn } from "@/lib/cn";
import { Icon, type IconName } from "@/components/icons/Icon";
import { useSidebarStore } from "@/stores/sidebar";
import { useTweaksStore } from "@/stores/tweaks";
import { stageStats } from "@/data/stageStats";
import type { Stage } from "@/data/types";

interface SidebarNavItem {
  id: string;
  label: string;
  icon: IconName;
  to: string;
  stageId?: Stage;
}

interface SidebarSection {
  sectionLabel: string;
}

type SidebarEntry = SidebarNavItem | SidebarSection;

const SIDEBAR_ITEMS: SidebarEntry[] = [
  { id: "dashboard", label: "대시보드", icon: "home", to: "/dashboard" },
  { sectionLabel: "접수 · 정보처리" },
  {
    id: "intake",
    label: "접수",
    icon: "inbox",
    to: "/intake",
    stageId: "intake",
  },
  {
    id: "processing",
    label: "정보처리",
    icon: "file-text",
    to: "/processing",
    stageId: "processing",
  },
  { sectionLabel: "심사 · 지급근거" },
  {
    id: "review",
    label: "심사",
    icon: "check-circle",
    to: "/review",
    stageId: "review",
  },
  {
    id: "evidence",
    label: "지급근거",
    icon: "shield",
    to: "/evidence",
    stageId: "evidence",
  },
  { sectionLabel: "지급 · 사후관리" },
  {
    id: "payment",
    label: "지급 · 결제",
    icon: "credit-card",
    to: "/payment",
    stageId: "payment",
  },
  {
    id: "post",
    label: "사후관리",
    icon: "monitor-chart",
    to: "/post",
    stageId: "post",
  },
  { sectionLabel: "시스템" },
  { id: "aiops", label: "AI 운영 관리", icon: "activity", to: "/admin/aiops" },
  {
    id: "control",
    label: "내부통제 · 권한",
    icon: "users",
    to: "/admin/control",
  },
  { id: "infra", label: "인프라 · 성능", icon: "server", to: "/admin/infra" },
];

function isSectionEntry(entry: SidebarEntry): entry is SidebarSection {
  return "sectionLabel" in entry;
}

const USERS: Record<string, { name: string; org: string }> = {
  admin: { name: "김철수", org: "퀀텀에이아이" },
  reviewer: { name: "이영희", org: "퀀텀에이아이" },
  processor: { name: "박정보", org: "퀀텀에이아이" },
};

/** 각 도메인 목록 첫 필터 기준 카운트 (stageStats — Dashboard pipelineData 와 동일 출처) */
const STAGE_BADGE_COUNTS: Map<Stage, number> = new Map(
  (Object.keys(stageStats) as Stage[]).map((id) => [id, stageStats[id].count]),
);

interface SidebarProps {
  className?: string;
}

/** 좌측 내비게이션 사이드바 */
const Sidebar = ({ className }: SidebarProps) => {
  const role = useTweaksStore((s) => s.role);
  const user = USERS[role] ?? USERS.admin;
  const matches = useMatches();

  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggleCollapse = useSidebarStore((s) => s.toggle);
  const badgeCounts = STAGE_BADGE_COUNTS;

  const isActive = (itemTo: string): boolean => {
    const currentPath = matches[matches.length - 1]?.pathname ?? "/";
    if (itemTo === "/dashboard")
      return currentPath === "/dashboard" || currentPath === "/";
    return currentPath.startsWith(itemTo);
  };

  return (
    <aside
      className={cn("flex flex-col bg-white", collapsed && "w-16", className)}
    >
      {/* 브랜드 헤더 */}
      <div
        className={cn(
          "flex h-14 shrink-0 items-center gap-2.5 border-b border-border px-5 transition-all",
          collapsed && "justify-center px-2",
        )}
      >
        <button
          className="grid shrink-0 cursor-pointer place-items-center overflow-hidden rounded-lg"
          style={{
            width: 32,
            height: 32,
            background:
              "linear-gradient(135deg, #0b5fff 0%, #2563eb 60%, #4f46e5 100%)",
            boxShadow:
              "0 4px 12px rgba(11,95,255,0.3), inset 0 1px 0 rgba(255,255,255,0.18)",
          }}
          onClick={collapsed ? toggleCollapse : undefined}
          title={collapsed ? "사이드바 펼치기" : "Claim-Q"}
          aria-label={collapsed ? "사이드바 펼치기" : "Claim-Q"}
        >
          <img
            src="/assets/q-logo.png"
            alt="Q"
            draggable="false"
            style={{
              width: 22,
              height: 22,
              objectFit: "contain",
              display: "block",
            }}
          />
        </button>
        {!collapsed && (
          <>
            <span className="text-lg font-bold tracking-tight text-gray-950">
              Claim-Q
            </span>
            <button
              className="ml-auto grid size-6 place-items-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              onClick={toggleCollapse}
              title="사이드바 접기"
              aria-label="사이드바 접기"
            >
              <Icon name="chevron-left" size={13} />
            </button>
          </>
        )}
      </div>

      {/* 네비게이션 */}
      <nav className="flex-1 overflow-y-auto p-3">
        {SIDEBAR_ITEMS.map((entry, i) => {
          if (isSectionEntry(entry)) {
            if (collapsed) return null;
            return (
              <div
                key={`section-${i}`}
                className="flex items-center gap-2 px-3 pb-2 pt-4 font-semibold uppercase tracking-widest text-gray-400"
                style={{ fontSize: 11 }}
              >
                <span>{entry.sectionLabel}</span>
                <span className="h-px flex-1 bg-gray-100" aria-hidden="true" />
              </div>
            );
          }

          const active = isActive(entry.to);
          const badge = entry.stageId
            ? badgeCounts.get(entry.stageId)
            : undefined;

          return (
            <Link
              key={entry.id}
              to={entry.to}
              className={cn(
                "relative flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors",
                "hover:bg-gray-50",
                active && "bg-blue-50 font-semibold text-primary",
                collapsed && "justify-center px-2",
              )}
              title={collapsed ? entry.label : undefined}
            >
              {active && (
                <span
                  className="absolute left-0 bg-primary"
                  style={{
                    top: 6,
                    bottom: 6,
                    width: 3,
                    borderRadius: "0 3px 3px 0",
                  }}
                />
              )}
              <span className="grid size-5 shrink-0 place-items-center">
                <Icon name={entry.icon} size={17} />
              </span>
              {!collapsed && (
                <>
                  <span className="flex-1 truncate">{entry.label}</span>
                  {badge != null && (
                    <span
                      className={cn(
                        "ml-auto min-w-6 rounded-full border text-center font-mono font-bold",
                        active
                          ? "border-blue-200 bg-white text-primary"
                          : "border-gray-200 bg-gray-100 text-gray-500",
                      )}
                      style={{ fontSize: "12.5px", padding: "1px 7px" }}
                    >
                      {badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </nav>

      {/* 사용자 정보 */}
      <div className="flex h-16 shrink-0 items-center gap-3 border-t border-border px-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-lg border border-border bg-gray-100 text-gray-500">
          <Icon name="user" size={18} />
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] font-semibold text-gray-950">
              {user.name}
            </div>
            <div className="truncate text-[13px] font-medium text-gray-400">
              {user.org}
            </div>
          </div>
        )}
        {!collapsed && (
          <button
            className="grid size-8 place-items-center rounded-md text-gray-400 hover:bg-gray-100"
            title="설정"
          >
            <Icon name="settings" size={16} />
          </button>
        )}
      </div>
    </aside>
  );
};

export { Sidebar };
export type { SidebarProps };
